import React, { useState, useMemo, useEffect } from 'react';
import { Box, Typography, Button, alpha } from '@mui/material';
import { motion } from 'framer-motion';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { 
  sectionColorsDark, 
  sectionColorsLight, 
  sectionIcons, 
  renderInlineContent, 
  parseMarkdownToBlocks 
} from '../../utils/workspaceUtils';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// Q&A Reveal Card
const QAHoverCard = ({ question, answer, accentColor, isLight }) => {
  const [hovered, setHovered] = useState(false);
  
  return (
    <Box
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        borderRadius: 2.5,
        border: `1px solid ${hovered ? accentColor + '60' : 'var(--border-main)'}`,
        overflow: 'hidden',
        transition: 'border-color 0.3s ease, background-color 0.3s ease, box-shadow 0.3s ease',
        bgcolor: hovered ? (isLight ? accentColor + '08' : 'rgba(255,255,255,0.02)') : 'transparent',
        boxShadow: hovered ? `0 8px 24px ${accentColor}15` : 'none',
        position: 'relative',
        zIndex: hovered ? 2 : 1,
        transform: 'translateZ(0)',
        willChange: 'height, opacity'
      }}
    >
      <Box sx={{ p: 2.2, display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        <Box sx={{
          width: 26, height: 26, borderRadius: '50%',
          bgcolor: accentColor + (isLight ? '15' : '25'),
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '0.7rem', fontWeight: 900, color: accentColor, flexShrink: 0,
          border: `1px solid ${accentColor}40`
        }}>Q</Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.5 }}>
            {renderInlineContent(question, accentColor, isLight)}
          </Typography>
          
          <motion.div
            initial={false}
            animate={{ 
              height: hovered ? 0 : 'auto',
              opacity: hovered ? 0 : 1,
              marginTop: hovered ? 0 : 8
            }}
            transition={{ duration: 0.2 }}
            style={{ overflow: 'hidden' }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
              <Box sx={{ 
                width: 8, height: 8, borderRadius: '50%', bgcolor: accentColor, opacity: 0.6, 
                animation: 'pulse 2s infinite',
                pointerEvents: 'none'
              }} />
              <Typography sx={{ fontSize: '0.72rem', color: accentColor, fontWeight: 700, letterSpacing: '0.3px' }}>Hover to reveal answer</Typography>
            </Box>
          </motion.div>
        </Box>
      </Box>

      <motion.div
        initial={false}
        animate={{ 
          height: hovered ? 'auto' : 0,
          opacity: hovered ? 1 : 0 
        }}
        transition={{ 
          duration: 0.4, 
          ease: [0.4, 0, 0.2, 1] 
        }}
        style={{ overflow: 'hidden' }}
      >
        <Box sx={{ px: 2.5, pb: 2.5, pt: 0, display: 'flex', gap: 2 }}>
          <Box sx={{
            width: 26, height: 26, borderRadius: '50%',
            bgcolor: isLight ? 'rgba(5,150,105,0.1)' : 'rgba(16,185,129,0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '0.7rem', fontWeight: 900, color: isLight ? '#065f46' : '#34d399', flexShrink: 0,
            border: isLight ? '1px solid rgba(5,150,105,0.2)' : '1px solid rgba(16,185,129,0.4)'
          }}>A</Box>
          <Typography sx={{
            fontSize: '0.94rem',
            color: isLight ? '#334155' : '#e2e8f0',
            lineHeight: 1.8,
            fontStyle: 'italic',
            fontWeight: isLight ? 500 : 400
          }}>
            {renderInlineContent(answer, accentColor, isLight)}
          </Typography>
        </Box>
      </motion.div>

      <style>{`
        @keyframes pulse {
          0% { transform: scale(0.85); opacity: 0.4; }
          50% { transform: scale(1.1); opacity: 0.8; }
          100% { transform: scale(0.85); opacity: 0.4; }
        }
      `}</style>
    </Box>
  );
};

export const InteractiveLesson = ({ text, isLight, onComplete, partOffset = 0 }) => {
  const sectionColors = isLight ? sectionColorsLight : sectionColorsDark;
  
  const parsed = useMemo(() => {
    if (!text) return { intro: [], sections: [] };
    const lines = text.split('\n');
    const introLines = [];
    const sections = [];
    let currentSection = null;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      if (line.startsWith('## ') && !line.startsWith('###')) {
        if (currentSection) sections.push(currentSection);
        currentSection = { heading: line.slice(3).trim(), rawLines: [] };
      } else if (currentSection) {
        currentSection.rawLines.push(line);
      } else if (line.trim() && !line.startsWith('# ')) {
        introLines.push(line);
      }
    }
    if (currentSection) sections.push(currentSection);

    return {
      intro: parseMarkdownToBlocks(introLines),
      sections: sections
        .filter(s => s.heading.toLowerCase() !== 'related problems')
        .map(s => ({ ...s, blocks: parseMarkdownToBlocks(s.rawLines) }))
    };
  }, [text]);

  const [visibleSectionsCount, setVisibleSectionsCount] = useState(1);

  useEffect(() => {
    setVisibleSectionsCount(1);
    if (parsed.sections.length === 0 && onComplete) {
      onComplete(true);
    }
  }, [text, parsed.sections.length, onComplete]);

  const renderBlock = (block, accentColor) => {
    switch (block.type) {
      case 'qa': return <QAHoverCard key={block.question} question={block.question} answer={block.answer} accentColor={accentColor} isLight={isLight} />;
      case 'code': return (
        <Box sx={{ borderRadius: 2.5, overflow: 'hidden', border: '1px solid var(--border-main)', bgcolor: '#0d0d10', my: 1 }}>
          <Box sx={{ px: 2, py: 1, bgcolor: '#1a1a1e', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ display: 'flex', gap: 0.6 }}>{['#ff5f56', '#ffbd2e', '#27c93f'].map((c, idx) => <Box key={idx} sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: c }} />)}</Box>
            <Typography sx={{ fontSize: '0.65rem', fontWeight: 900, color: '#52525b', textTransform: 'uppercase', letterSpacing: '1px' }}>{block.lang || 'code'}</Typography>
          </Box>
          <Box component="pre" sx={{ m: 0, p: 2.5, overflowX: 'auto', fontSize: '0.82rem', fontFamily: 'monospace', color: '#a1a1aa', lineHeight: 1.6 }}>{block.content}</Box>
        </Box>
      );
      case 'callout': {
        const isAlert = !!block.alertType;
        const alertConfig = {
          TIP: { icon: '💡', name: 'Tip', color: '#0ea5e9' },
          NOTE: { icon: '📝', name: 'Note', color: '#6366f1' },
          IMPORTANT: { icon: '✨', name: 'Important', color: '#8b5cf6' },
          WARNING: { icon: '⚠️', name: 'Warning', color: '#f59e0b' },
          CAUTION: { icon: '🛑', name: 'Caution', color: '#ef4444' }
        };
        const config = alertConfig[block.alertType] || { icon: '💡', name: 'Insight', color: accentColor };

        return (
          <Box sx={{ 
            p: 2.5, borderRadius: 2.5, 
            bgcolor: isAlert ? alpha(config.color, 0.05) : accentColor + '08', 
            borderLeft: `4px solid ${isAlert ? config.color : accentColor}`, 
            display: 'flex', flexDirection: 'column', gap: 1, my: 1 
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
              <Typography sx={{ fontSize: '1.1rem' }}>{config.icon}</Typography>
              <Typography sx={{ fontSize: '0.7rem', fontWeight: 900, color: config.color, textTransform: 'uppercase', letterSpacing: '1px' }}>
                {config.name}
              </Typography>
            </Box>
            <Typography sx={{ fontSize: '0.96rem', color: isLight ? '#1e293b' : '#f1f5f9', fontStyle: isAlert ? 'normal' : 'italic', lineHeight: 1.7, fontWeight: isAlert ? 500 : 400 }}>
              {renderInlineContent(block.text, isAlert ? config.color : accentColor, isLight)}
            </Typography>
          </Box>
        );
      }
      case 'list': return (
        <Box component="ul" sx={{ m: 0, pl: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {block.items.map((item, idx) => (
            <Box key={idx} component="li" sx={{
              listStyle: 'none', display: 'flex', gap: 1.5, p: 1.2, borderRadius: 1.5,
              transition: 'background 0.2s', '&:hover': { bgcolor: accentColor + '08' }
            }}>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: accentColor, mt: 1, flexShrink: 0 }} />
              <Typography sx={{ fontSize: '0.94rem', color: isLight ? '#334155' : '#cbd5e1', lineHeight: 1.7 }}>{renderInlineContent(item, accentColor, isLight)}</Typography>
            </Box>
          ))}
        </Box>
      );
      case 'heading': return (
        <Typography sx={{
          fontSize: block.level === 3 ? '1.05rem' : '0.95rem',
          fontWeight: 800,
          color: isLight ? '#0f172a' : '#f8fafc',
          mt: 2.5, mb: 1.5,
          letterSpacing: '-0.3px',
          borderBottom: `1px solid ${accentColor}30`,
          display: 'inline-block',
          pb: 0.5
        }}>
          {renderInlineContent(block.text, accentColor, isLight)}
        </Typography>
      );
      case 'divider': return (
        <Box sx={{
          height: '1px',
          width: '100%',
          my: 3,
          background: `linear-gradient(90deg, transparent 0%, ${accentColor}40 50%, transparent 100%)`
        }} />
      );
      case 'table': return (
        <Box 
          key={block.content}
          sx={{ 
            my: 2.5, 
            overflowX: 'auto',
            '& table': {
              width: '100%',
              borderCollapse: 'separate',
              borderSpacing: 0,
              borderRadius: 2.5,
              border: `1px solid ${isLight ? '#e2e8f0' : '#1f1f22'}`,
              overflow: 'hidden',
              fontSize: '0.88rem',
            },
            '& th': {
              bgcolor: isLight ? '#f8fafc' : '#0d0d10',
              color: isLight ? '#475569' : '#94a3b8',
              fontWeight: 800,
              textAlign: 'left',
              p: 2,
              borderBottom: `2px solid ${accentColor}40`,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              fontSize: '0.72rem'
            },
            '& td': {
              p: 2,
              borderBottom: `1px solid ${isLight ? '#f1f5f9' : '#1a1a1e'}`,
              color: isLight ? '#1e293b' : '#cbd5e1',
              lineHeight: 1.5,
              fontWeight: 500
            },
            '& tr:last-child td': {
              borderBottom: 'none'
            },
            '& tr:hover td': {
              bgcolor: isLight ? accentColor + '04' : 'rgba(255,255,255,0.015)',
              transition: 'background 0.2s'
            }
          }}
        >
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {block.content}
          </ReactMarkdown>
        </Box>
      );
      default: return <Typography sx={{ fontSize: '0.98rem', color: isLight ? '#1e293b' : '#cbd5e1', lineHeight: 1.85, mb: 1.5 }}>{renderInlineContent(block.text, accentColor, isLight)}</Typography>;
    }
  };

  const visibleSections = parsed.sections.slice(0, visibleSectionsCount);
  const nextSection = parsed.sections[visibleSectionsCount];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {parsed.intro.length > 0 && (
        <Box sx={{ mb: 1 }}>
          {parsed.intro.map((block, idx) => <Box key={idx} sx={{ mb: 1.5 }}>{renderBlock(block, '#3b82f6')}</Box>)}
        </Box>
      )}

      {visibleSections.map((section, idx) => {
        const color = sectionColors[idx % sectionColors.length];
        const icon = sectionIcons[idx % sectionIcons.length];
        return (
          <motion.div
            key={idx}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            id={`section-${idx}`}
          >
            <Box sx={{
              p: 3, borderRadius: 3.5, border: '1px solid var(--border-main)',
              transition: 'border-color 0.3s ease, box-shadow 0.3s ease', 
              position: 'relative', 
              overflow: 'hidden',
              transform: 'translateZ(0)',
              backfaceVisibility: 'hidden',
              '&:hover': { borderColor: color + '40', boxShadow: `0 8px 32px ${color}08` }
            }}>
              <Box sx={{ position: 'absolute', top: 0, left: 0, bottom: 0, width: 4, bgcolor: color, opacity: 0.6 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ fontSize: '1.4rem' }}>{icon}</Box>
                  <Typography sx={{ fontSize: '1.15rem', fontWeight: 900, color: 'var(--text-primary)', letterSpacing: '-0.3px' }}>{section.heading}</Typography>
                </Box>
                <Box sx={{ px: 1.5, py: 0.5, borderRadius: 10, bgcolor: color + '15', border: `1px solid ${color}30` }}>
                  <Typography sx={{ fontSize: '0.65rem', fontWeight: 900, color, letterSpacing: '0.8px' }}>PART {idx + 1 + partOffset}</Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {section.blocks.map((block, bIdx) => <Box key={bIdx}>{renderBlock(block, color)}</Box>)}
              </Box>
            </Box>
          </motion.div>
        );
      })}

      {nextSection && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 4 }}>
          <Button
            variant="contained"
            onClick={() => {
              const newCount = visibleSectionsCount + 1;
              setVisibleSectionsCount(newCount);
              if (newCount === parsed.sections.length && onComplete) {
                onComplete(true);
              }
              setTimeout(() => {
                const el = document.getElementById(`section-${visibleSectionsCount}`);
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }, 100);
            }}
            endIcon={<ChevronRightIcon />}
            sx={{
              borderRadius: 3,
              px: 4, py: 1.8,
              textTransform: 'none',
              fontWeight: 900,
              fontSize: '0.95rem',
              bgcolor: 'var(--bg-paper)',
              color: sectionColors[visibleSectionsCount % sectionColors.length],
              border: `2px solid ${sectionColors[visibleSectionsCount % sectionColors.length]}40`,
              boxShadow: `0 8px 24px ${sectionColors[visibleSectionsCount % sectionColors.length]}15`,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                bgcolor: 'var(--bg-paper)',
                boxShadow: `0 12px 32px ${sectionColors[visibleSectionsCount % sectionColors.length]}25`,
                borderColor: sectionColors[visibleSectionsCount % sectionColors.length],
                transform: 'translateY(-2px)'
              },
              '&:active': { transform: 'translateY(0)' }
            }}
          >
            Continue Explorer: {nextSection.heading}
          </Button>
        </Box>
      )}
    </Box>
  );
};
